import type { Issue, IssueCode } from './issues.ts';
import {
	CLUE_DIRECTIONS,
	PUZZLE_SCHEMA_VERSION,
	PUZZLE_TYPES,
	type Answer,
	type Axis,
	type Clue,
	type ClueContent,
	type ClueDirection,
	type Coord,
	type Difficulty,
	isLevel,
	type Puzzle,
	type PuzzleTheme,
	type PuzzleType,
} from './types.ts';
import { checkGrid } from './check-grid.ts';

export type ParseResult = { ok: true; puzzle: Puzzle } | { ok: false; issues: Issue[] };

/**
 * Structural parse of untrusted puzzle JSON: types, required fields, enums and the grid's
 * shape. It does not judge whether the puzzle is *playable*; `validatePuzzle` does that.
 * Returns a fresh object holding only known fields. Never throws.
 *
 * Each reader below records an issue and returns a placeholder when its input is bad, so
 * a single pass reports every problem; the placeholders never escape because any issue
 * makes the result `ok: false`.
 */
export function parsePuzzle(raw: unknown): ParseResult {
	const issues: Issue[] = [];
	if (!isRecord(raw)) {
		// Nothing else is worth saying about a value that isn't even an object.
		report(issues, 'invalid-type', '', `Puzzle must be an object, got ${describe(raw)}.`);
		return { ok: false, issues };
	}
	try {
		const puzzle = readPuzzle(raw, issues);
		return issues.length === 0 ? { ok: true, puzzle } : { ok: false, issues };
	} catch (error) {
		// Plain JSON can't get here; exotic inputs (throwing getters, proxies) can.
		const reason = error instanceof Error ? error.message : String(error);
		issues.push({ code: 'invalid-type', message: `Could not read the puzzle: ${reason}` });
		return { ok: false, issues };
	}
}

const PUZZLE_KEYS = [
	'schemaVersion',
	'id',
	'title',
	'difficulty',
	'level',
	'width',
	'height',
	'grid',
	'answers',
	'clues',
	'theme',
	'type',
	'author',
	'notes',
];
const ANSWER_KEYS = ['id', 'text', 'start', 'axis', 'display'];
const CLUE_KEYS = ['id', 'cell', 'direction', 'content', 'answerId'];
const COORD_KEYS = ['row', 'col'];
const THEME_KEYS = ['answer', 'members', 'reveal'];
const CONTENT_KEYS: Record<ClueContent['kind'], string[]> = {
	text: ['kind', 'text'],
	icon: ['kind', 'icon', 'label'],
	image: ['kind', 'src', 'alt'],
};

function readPuzzle(raw: unknown, issues: Issue[]): Puzzle {
	const obj = readObject(raw, '', PUZZLE_KEYS, issues);

	const width = readPositiveInt(obj, 'width', '', issues);
	const height = readPositiveInt(obj, 'height', '', issues);
	const grid = readArray(obj, 'grid', '', issues, (row, path) => readStringValue(row, path, issues));
	// Only measure the grid against width/height when all three are trustworthy.
	const rowsAreStrings = Array.isArray(obj.grid) && obj.grid.every((row) => typeof row === 'string');
	if (width > 0 && height > 0 && rowsAreStrings) {
		issues.push(...checkGrid({ width, height, grid }));
	}

	const puzzle: Puzzle = {
		schemaVersion: readSchemaVersion(obj, issues),
		id: readString(obj, 'id', '', issues),
		title: readString(obj, 'title', '', issues),
		difficulty: readDifficulty(obj, issues),
		width,
		height,
		grid,
		answers: readArray(obj, 'answers', '', issues, (value, path) => readAnswer(value, path, issues)),
		clues: readArray(obj, 'clues', '', issues, (value, path) => readClue(value, path, issues)),
	};

	if (obj.level !== undefined) {
		if (isLevel(obj.level)) puzzle.level = obj.level;
		else report(issues, 'invalid-value', 'level', `level must be "easy", "medium" or "hard", got ${describe(obj.level)}.`);
	}
	if (obj.theme !== undefined) puzzle.theme = readTheme(obj.theme, 'theme', issues);
	if (obj.type !== undefined) {
		if ((PUZZLE_TYPES as readonly unknown[]).includes(obj.type)) puzzle.type = obj.type as PuzzleType;
		else report(issues, 'invalid-value', 'type', `type must be one of ${PUZZLE_TYPES.map((t) => `"${t}"`).join(', ')}, got ${describe(obj.type)}.`);
	}
	const author = readOptionalString(obj, 'author', '', issues);
	if (author !== undefined) puzzle.author = author;
	const notes = readOptionalString(obj, 'notes', '', issues);
	if (notes !== undefined) puzzle.notes = notes;

	return puzzle;
}

function readAnswer(raw: unknown, path: string, issues: Issue[]): Answer {
	const obj = readObject(raw, path, ANSWER_KEYS, issues);
	const answer: Answer = {
		id: readString(obj, 'id', path, issues),
		// Only "is a string" here; the A–Z rule is the validator's, so it is reported
		// alongside the other answer problems instead of hiding them.
		text: readString(obj, 'text', path, issues, { allowEmpty: true }),
		start: readCoord(obj.start, join(path, 'start'), issues),
		axis: readAxis(obj, path, issues),
	};
	const display = readOptionalString(obj, 'display', path, issues);
	if (display !== undefined) answer.display = display;
	return answer;
}

function readClue(raw: unknown, path: string, issues: Issue[]): Clue {
	const obj = readObject(raw, path, CLUE_KEYS, issues);
	return {
		id: readString(obj, 'id', path, issues),
		cell: readCoord(obj.cell, join(path, 'cell'), issues),
		direction: readDirection(obj, path, issues),
		content: readContent(obj.content, join(path, 'content'), issues),
		answerId: readString(obj, 'answerId', path, issues),
	};
}

function readContent(raw: unknown, path: string, issues: Issue[]): ClueContent {
	const fallback: ClueContent = { kind: 'text', text: '' };
	if (raw === undefined) {
		report(issues, 'missing-field', path, 'Clue content is missing.');
		return fallback;
	}
	if (!isRecord(raw)) {
		report(issues, 'invalid-type', path, 'Clue content must be an object.');
		return fallback;
	}
	const kind = raw.kind;
	if (kind !== 'text' && kind !== 'icon' && kind !== 'image') {
		report(
			issues,
			'invalid-clue-content',
			join(path, 'kind'),
			`Clue content kind must be "text", "icon" or "image", got ${describe(kind)}.`,
		);
		return fallback;
	}
	const obj = readObject(raw, path, CONTENT_KEYS[kind], issues);
	switch (kind) {
		case 'text':
			return { kind, text: readString(obj, 'text', path, issues) };
		case 'icon':
			return {
				kind,
				icon: readString(obj, 'icon', path, issues),
				label: readString(obj, 'label', path, issues),
			};
		case 'image':
			return {
				kind,
				src: readString(obj, 'src', path, issues),
				alt: readString(obj, 'alt', path, issues),
			};
	}
}

function readTheme(raw: unknown, path: string, issues: Issue[]): PuzzleTheme {
	const obj = readObject(raw, path, THEME_KEYS, issues);
	const theme: PuzzleTheme = {
		answer: readString(obj, 'answer', path, issues),
		members: readArray(obj, 'members', path, issues, (value, itemPath) =>
			readStringValue(value, itemPath, issues),
		),
	};
	const reveal = readOptionalString(obj, 'reveal', path, issues);
	if (reveal !== undefined) theme.reveal = reveal;
	return theme;
}

function readCoord(raw: unknown, path: string, issues: Issue[]): Coord {
	if (raw === undefined) {
		report(issues, 'missing-field', path, 'Coordinate is missing.');
		return { row: 0, col: 0 };
	}
	const obj = readObject(raw, path, COORD_KEYS, issues);
	// Negative or too-large values are well-formed; the validator reports them against
	// the grid, where "out of bounds" means something.
	return { row: readInt(obj, 'row', path, issues), col: readInt(obj, 'col', path, issues) };
}

function readSchemaVersion(obj: Record<string, unknown>, issues: Issue[]): Puzzle['schemaVersion'] {
	const value = obj.schemaVersion;
	if (value === undefined) {
		report(issues, 'missing-field', 'schemaVersion', 'schemaVersion is missing.');
	} else if (value !== PUZZLE_SCHEMA_VERSION) {
		report(
			issues,
			'unsupported-schema-version',
			'schemaVersion',
			`schemaVersion ${describe(value)} is not supported; this build reads version ${PUZZLE_SCHEMA_VERSION}.`,
		);
	}
	return PUZZLE_SCHEMA_VERSION;
}

function readDifficulty(obj: Record<string, unknown>, issues: Issue[]): Difficulty {
	const value = readInt(obj, 'difficulty', '', issues);
	if (isDifficulty(value)) return value;
	if (Number.isInteger(obj.difficulty)) {
		report(issues, 'invalid-value', 'difficulty', `difficulty must be 1–5, got ${value}.`);
	}
	return 1;
}

function isDifficulty(value: number): value is Difficulty {
	return value === 1 || value === 2 || value === 3 || value === 4 || value === 5;
}

function readAxis(obj: Record<string, unknown>, path: string, issues: Issue[]): Axis {
	const value = obj.axis;
	if (value === 'across' || value === 'down') return value;
	if (value === undefined) report(issues, 'missing-field', join(path, 'axis'), 'axis is missing.');
	else {
		report(
			issues,
			'malformed-axis',
			join(path, 'axis'),
			`axis must be "across" or "down", got ${describe(value)}.`,
		);
	}
	return 'across';
}

function readDirection(obj: Record<string, unknown>, path: string, issues: Issue[]): ClueDirection {
	const value = obj.direction;
	if (isClueDirection(value)) return value;
	if (value === undefined) {
		report(issues, 'missing-field', join(path, 'direction'), 'direction is missing.');
	} else {
		report(
			issues,
			'malformed-direction',
			join(path, 'direction'),
			`direction must be one of ${CLUE_DIRECTIONS.map((d) => `"${d}"`).join(', ')}, got ${describe(value)}.`,
		);
	}
	return 'right';
}

export function isClueDirection(value: unknown): value is ClueDirection {
	return typeof value === 'string' && (CLUE_DIRECTIONS as readonly string[]).includes(value);
}

// --- Generic readers --------------------------------------------------------------------

/** Reads an object and flags keys outside `allowed`, which are usually typos. */
function readObject(
	raw: unknown,
	path: string,
	allowed: readonly string[],
	issues: Issue[],
): Record<string, unknown> {
	if (!isRecord(raw)) {
		report(issues, 'invalid-type', path, `${path || 'Puzzle'} must be an object, got ${describe(raw)}.`);
		return {};
	}
	for (const key of Object.keys(raw)) {
		if (!allowed.includes(key)) {
			report(issues, 'unknown-field', join(path, key), `Unknown field "${key}".`);
		}
	}
	return raw;
}

function readArray<T>(
	obj: Record<string, unknown>,
	key: string,
	path: string,
	issues: Issue[],
	readItem: (value: unknown, path: string) => T,
): T[] {
	const value = obj[key];
	const fieldPath = join(path, key);
	if (value === undefined) {
		report(issues, 'missing-field', fieldPath, `${key} is missing.`);
		return [];
	}
	if (!Array.isArray(value)) {
		report(issues, 'invalid-type', fieldPath, `${key} must be an array, got ${describe(value)}.`);
		return [];
	}
	return value.map((item, i) => readItem(item, `${fieldPath}[${i}]`));
}

function readString(
	obj: Record<string, unknown>,
	key: string,
	path: string,
	issues: Issue[],
	{ allowEmpty = false } = {},
): string {
	const fieldPath = join(path, key);
	if (obj[key] === undefined) {
		report(issues, 'missing-field', fieldPath, `${key} is missing.`);
		return '';
	}
	const value = readStringValue(obj[key], fieldPath, issues);
	if (!allowEmpty && value.trim() === '' && typeof obj[key] === 'string') {
		report(issues, 'invalid-value', fieldPath, `${key} must not be empty.`);
	}
	return value;
}

function readOptionalString(
	obj: Record<string, unknown>,
	key: string,
	path: string,
	issues: Issue[],
): string | undefined {
	return obj[key] === undefined ? undefined : readStringValue(obj[key], join(path, key), issues);
}

function readStringValue(value: unknown, path: string, issues: Issue[]): string {
	if (typeof value === 'string') return value;
	report(issues, 'invalid-type', path, `${path} must be a string, got ${describe(value)}.`);
	return '';
}

function readInt(obj: Record<string, unknown>, key: string, path: string, issues: Issue[]): number {
	const value = obj[key];
	const fieldPath = join(path, key);
	if (value === undefined) {
		report(issues, 'missing-field', fieldPath, `${key} is missing.`);
		return 0;
	}
	if (typeof value !== 'number') {
		report(issues, 'invalid-type', fieldPath, `${key} must be a number, got ${describe(value)}.`);
		return 0;
	}
	if (!Number.isInteger(value)) {
		report(issues, 'invalid-value', fieldPath, `${key} must be a whole number, got ${value}.`);
		return 0;
	}
	return value;
}

function readPositiveInt(obj: Record<string, unknown>, key: string, path: string, issues: Issue[]): number {
	const value = readInt(obj, key, path, issues);
	if (Number.isInteger(obj[key]) && value < 1) {
		report(issues, 'invalid-value', join(path, key), `${key} must be at least 1, got ${value}.`);
		return 0;
	}
	return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function join(path: string, key: string): string {
	return path ? `${path}.${key}` : key;
}

function report(issues: Issue[], code: IssueCode, path: string, message: string): void {
	issues.push(path ? { code, message, path } : { code, message });
}

/** A short description of an unexpected value for messages: `"abc"`, `null`, `an array`. */
function describe(value: unknown): string {
	if (value === null) return 'null';
	if (Array.isArray(value)) return 'an array';
	if (typeof value === 'string') return JSON.stringify(value);
	if (typeof value === 'object') return 'an object';
	return String(value);
}
